import * as Sentry from '@sentry/browser';

let initialized = false;

export interface ErrorTrackingConfig {
  dsn: string;
  environment?: string;
  release?: string;
  tracesSampleRate?: number;
  tunnel?: string;
}

/**
 * Initialize GlitchTip/Sentry error tracking.
 * Safe to call multiple times - only initializes once.
 */
export function initErrorTracking(config: ErrorTrackingConfig): void {
  if (initialized || !config.dsn) return;

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment ?? 'production',
    release: config.release,
    tracesSampleRate: config.tracesSampleRate ?? 0.1,
    tunnel: config.tunnel,

    beforeSend(event) {
      return event;
    },

    // Ignore common non-actionable errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^Network request failed$/,
      /^Failed to fetch$/,
      /^Load failed$/,
    ],
  });

  initialized = true;
}

/**
 * Manually capture an exception with optional context.
 */
export function captureException(
  error: Error | unknown,
  context?: Record<string, unknown>
): void {
  Sentry.captureException(error, { extra: context });
}

/**
 * Manually capture a message.
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  Sentry.captureMessage(message, level);
}

/**
 * Set user context for error reports.
 */
export function setUser(user: { id?: string; email?: string; tier?: string } | null): void {
  Sentry.setUser(user);
}

/**
 * Add a breadcrumb for debugging context.
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    message,
    category: category ?? 'app',
    data,
    level: 'info',
  });
}
